import {useMemo} from 'react';
import {assert} from 'assert-ts';
import type {Data, Schema} from 'schemaDefinition/types';
import type {ManifestationStatus} from 'services/metadata/types';
import {
  getModifiedSchema,
  getModifiedSchemaWithModifier,
} from 'schemaDefinition/functions';
import type {ModifiableSchemaReference, WorkType} from '../types';
import {MetadataModifierKeys} from '../types';
import {useGetDataBasedSchemaModifier} from './useGetDataBasedSchemaModifier';
import {useSchema} from './useSchema';
import {useSchemaModifiers} from './useSchemaModifiers';

export const useModifiedSchema = (
  savedData: Data,
  workType: WorkType,
  schemaRef: ModifiableSchemaReference,
  forStatus: ManifestationStatus | undefined,
): Schema => {
  const schema = useSchema(schemaRef);
  const getDataBasedModifier = useGetDataBasedSchemaModifier(schemaRef);
  const modifiers = useSchemaModifiers(schemaRef);

  return useMemo(() => {
    const statuses = MetadataModifierKeys[workType];
    const baseSchema = assert(schema, 'useModifiedSchema: schema expected');
    const dataBasedModifier = getDataBasedModifier?.(savedData);
    const modifiedBaseSchema = dataBasedModifier
      ? getModifiedSchemaWithModifier(baseSchema, dataBasedModifier)
      : baseSchema;

    return forStatus === undefined
      ? modifiedBaseSchema
      : getModifiedSchema(
          modifiedBaseSchema,
          modifiers,
          `${workType}.${forStatus}`,
          statuses,
        );
  }, [forStatus, getDataBasedModifier, modifiers, savedData, schema, workType]);
};
